import moment from "moment";
import { getSeriesCardState } from "./seriesCardState";

/**
 * Ordering for the event-series cards (Timeline + HomePressCon CitySeries):
 * "Udah Lewat" (event window fully passed) sinks to the back, everything else
 * rises to the front, sorted by event start then end date so the soonest /
 * upcoming event is on top. Same intent as the Timeline sort's past-to-back rule.
 */

interface SeriesLike {
  event_start_date?: string;
  event_end_date?: string;
}

// Event window fully passed — now is after event_end_date (mirrors seriesCardState).
export const isPastEvent = (e: SeriesLike = {}) =>
  !!e?.event_end_date && moment().isAfter(moment(e.event_end_date), "day");

const byEventDate = (a: SeriesLike, b: SeriesLike) => {
  const startDiff =
    moment(a?.event_start_date).valueOf() - moment(b?.event_start_date).valueOf();
  if (startDiff !== 0) return startDiff;
  return moment(a?.event_end_date).valueOf() - moment(b?.event_end_date).valueOf();
};

export const sortSeriesByDate = <T extends SeriesLike>(events: T[]): T[] =>
  [...events].sort((a, b) => {
    const aPast = isPastEvent(a);
    const bPast = isPastEvent(b);
    if (aPast !== bPast) return aPast ? 1 : -1;
    return byEventDate(a, b);
  });

// Lifecycle bucket for the "Daftar" card list: open registration first, then
// running/live, then upcoming ("Akan Datang"), then finished ("Udah Lewat").
// Reuses getSeriesCardState so the order tracks the badge the card actually shows.
const stateRank = (e: SeriesLike): number => {
  const { action } = getSeriesCardState(e);
  if (action === "register") return 0; // open regist
  if (isPastEvent(e)) return 3; // udah lewat
  if (action === "none") return 2; // akan datang
  return 1; // berlangsung / tonton live (running now)
};

export const sortSeriesByState = <T extends SeriesLike>(events: T[]): T[] =>
  [...events].sort((a, b) => stateRank(a) - stateRank(b) || byEventDate(a, b));
